import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Contact form validation rules
 */
const ValidationRules = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z\s'-]+$/
  },
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  },
  subject: {
    required: true,
    validOptions: ['general', 'partnership', 'programs', 'funding', 'media', 'careers']
  },
  message: {
    required: true,
    minLength: 10,
    maxLength: 1000
  }
};

/**
 * Validate form data
 */
export const validateContactForm = (formData) => {
  const errors = {};

  // Validate name
  if (!formData.name?.trim()) {
    errors.name = 'Name is required';
  } else if (formData.name.trim().length < ValidationRules.name.minLength) {
    errors.name = `Name must be at least ${ValidationRules.name.minLength} characters`;
  } else if (formData.name.trim().length > ValidationRules.name.maxLength) {
    errors.name = `Name must be less than ${ValidationRules.name.maxLength} characters`;
  } else if (!ValidationRules.name.pattern.test(formData.name.trim())) {
    errors.name = 'Name can only contain letters, spaces, hyphens, and apostrophes';
  }

  // Validate email
  if (!formData.email?.trim()) {
    errors.email = 'Email is required';
  } else if (!ValidationRules.email.pattern.test(formData.email.trim())) {
    errors.email = 'Please enter a valid email address';
  }

  // Validate subject
  if (!formData.subject) {
    errors.subject = 'Please select a subject';
  } else if (!ValidationRules.subject.validOptions.includes(formData.subject)) {
    errors.subject = 'Please select a valid subject';
  }

  // Validate message
  if (!formData.message?.trim()) {
    errors.message = 'Message is required';
  } else if (formData.message.trim().length < ValidationRules.message.minLength) {
    errors.message = `Message must be at least ${ValidationRules.message.minLength} characters`;
  } else if (formData.message.trim().length > ValidationRules.message.maxLength) {
    errors.message = `Message must be less than ${ValidationRules.message.maxLength} characters`;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Sanitize form data
 */
const sanitizeFormData = (formData) => {
  return {
    name: formData.name?.trim() || '',
    email: formData.email?.trim().toLowerCase() || '',
    subject: formData.subject || '',
    message: formData.message?.trim() || ''
  };
};

/**
 * Submit contact form to Firebase
 */
export const submitContactForm = async (formData) => {
  try {
    // Sanitize input data
    const sanitizedData = sanitizeFormData(formData);
    
    // Validate form data
    const validation = validateContactForm(sanitizedData);
    if (!validation.isValid) {
      throw new Error(JSON.stringify(validation.errors));
    }

    // Prepare submission data
    const submissionData = {
      ...sanitizedData,
      timestamp: serverTimestamp(),
      status: 'new',
      ipAddress: null, // You can add IP tracking if needed
      userAgent: navigator.userAgent,
      source: 'yclab-website',
      id: null // Will be generated by Firestore
    };

    // Submit to Firestore
    const docRef = await addDoc(collection(db, 'contact-submissions'), submissionData);
    
    // Return success response with document ID
    return {
      success: true,
      id: docRef.id,
      message: 'Your message has been sent successfully! We\'ll get back to you within 24 hours.'
    };

  } catch (error) {
    console.error('Contact form submission error:', error);
    
    // Handle validation errors
    if (error.message.startsWith('{')) {
      try {
        const validationErrors = JSON.parse(error.message);
        return {
          success: false,
          errors: validationErrors,
          message: 'Please correct the errors in your form and try again.'
        };
      } catch (parseError) {
        // Fall through to generic error handling
      }
    }
    
    // Handle Firebase errors
    if (error.code) {
      switch (error.code) {
        case 'permission-denied':
          return {
            success: false,
            message: 'Sorry, we\'re unable to process your message right now. Please try again later or contact us directly.'
          };
        case 'unavailable':
          return {
            success: false,
            message: 'Our contact service is temporarily unavailable. Please try again in a few minutes.'
          };
        default:
          return {
            success: false,
            message: 'An unexpected error occurred. Please try again or contact us directly at info@yocilab.org'
          };
      }
    }
    
    // Generic error handling
    return {
      success: false,
      message: 'Sorry, something went wrong. Please try again or contact us directly at info@yocilab.org'
    };
  }
};

/**
 * Get subject label for display
 */
export const getSubjectLabel = (subjectValue) => {
  const subjects = {
    general: 'General Inquiry',
    partnership: 'Partnership Opportunity',
    programs: 'Programs & Services',
    funding: 'Funding & Investment',
    media: 'Media & Press',
    careers: 'Careers & Opportunities'
  };
  return subjects[subjectValue] || subjectValue;
};

/**
 * Format timestamp for display
 */
export const formatSubmissionDate = (timestamp) => {
  if (!timestamp) return 'Unknown';
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  }).format(date);
};

/**
 * Export validation rules for use in components
 */
export { ValidationRules };